import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealInlinePrice from 'components/Flights/FlightMerchandising/FlightDealInlinePrice'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import SwitchListItem from 'LuxKit/List/SwitchListItem'
import Sheet from 'components/utils/Sheet'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { Fragment, useCallback, useContext } from 'react'
import * as React from 'react'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import Divider from 'LuxKit/Divider'
import { rem } from 'polished'
import styled from 'styled-components'
import TextLink from 'LuxKit/TextLink'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { FormattedMessage, useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import useOfferPageFlightToggleDisplayPrice, { type FlightToggleDisplayPrice } from 'components/OfferPage/FlightDeal/useOfferPageFlightToggleDisplayPrice'
import { getFlightPricePerPerson } from 'lib/offer/flightPriceUtils'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledFlightsBundledLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import isAuDomesticAirportRoute from 'lib/flights/isAuDomesticAirportRoute'

const DividerContainer = styled.div`
  padding-inline: ${rem(16)};
`

interface Props {
  roomsConnected?: boolean
  flights?: boolean
  flightsEnabled?: boolean
  airport?: App.Airport
  charterBundleRoute?: App.CharterBundleRoute
  bundledWithFlightsOnly?: boolean
  flightPricePerPerson?: number
  totalFlightPrice?: number
}

interface ContentProps extends Omit<Props, 'flightPricePerPerson'> {
  displayFlightPrice?: FlightToggleDisplayPrice
}

interface ViewProps extends ContentProps {
  hasMultipleRooms: boolean
  onConnectedRoomsChange: () => void
  onFlightsChange: () => void
  onChangeAirport: () => void
}

function OfferPageTogglesSectionView({
  roomsConnected,
  flights,
  flightsEnabled,
  airport,
  charterBundleRoute,
  bundledWithFlightsOnly,
  displayFlightPrice,
  hasMultipleRooms,
  onConnectedRoomsChange,
  onFlightsChange,
  onChangeAirport,
}: ViewProps) {
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const airports = useAppSelector(state => state.geo.airports)
  const isBundledLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsBundledLuxPlusPricing)
  const isAuDomesticFlight = isAuDomesticAirportRoute({
    airports,
    departureAirportCode: airport?.code,
    destinationAirportCode: offerPageState.offer?.flightDestinationPort ?? offerPageState.bedbankOffer?.flights.airportCode,
  })
  const luxPlusFlightDiscountAvailable = isBundledLuxPlusPricingEnabled &&
    !bundledWithFlightsOnly &&
    !isAuDomesticFlight

  const airportLink = airport ? <TextLink onClick={onChangeAirport}>{airport.code}</TextLink> : undefined
  let flightTitle: React.ReactNode
  if (bundledWithFlightsOnly && airport) {
    flightTitle = <FormattedMessage
      id="3R1Za4n1q9L04F3qUtnq"
      defaultMessage="Select flights from {airportLink}"
      description="Flight toggle title for bundled flights with airport selection link"
      values={{ airportLink }}
    />
  } else if (bundledWithFlightsOnly) {
    flightTitle = intl.formatMessage({
      id: 'iw2CuzTlPxgZJye-YlHI',
      defaultMessage: 'Select flights',
      description: 'Flight toggle title for bundled flights without airport',
    })
  } else if (airport) {
    flightTitle = <FormattedMessage
      id="z0DAg7aK29v8Udh2m1Bz"
      defaultMessage="Add a flight from {airportLink}"
      description="Flight toggle title for optional flights with airport selection link"
      values={{ airportLink }}
    />
  } else {
    flightTitle = intl.formatMessage(commonMessages.addFlights)
  }

  let flightSubtitle: React.ReactNode | undefined
  if (displayFlightPrice && !bundledWithFlightsOnly) {
    flightSubtitle = <FormattedMessage
      id="28arrFf5PBOi_kFotpt5"
      defaultMessage="From {price}/{unit}"
      description="Flight price subtitle in flight toggle on the form"
      values={{
        price: displayFlightPrice?.source === 'tacticalDeal' ? <FlightDealInlinePrice compact flightDeal={displayFlightPrice.deal} /> : <FormatCurrency value={displayFlightPrice.amount} format="rounded" />,
        unit: displayFlightPrice.unit,
      }}
    />
  }

  const toggles = [
    {
      key: 'connectingRooms',
      visible: hasMultipleRooms && !charterBundleRoute,
      element: <SwitchListItem
        title={intl.formatMessage({
          id: 'yWFYFf0CqTZQhvrygklw',
          defaultMessage: 'Request connecting rooms',
          description: 'Toggle title to request connecting rooms in multi-room booking',
        })}
        subtitle={intl.formatMessage({
          id: 'cyz5YyVmTKvYvUWX0Zlb',
          defaultMessage: 'Subject to availability. Confirmed at check-in.',
          description: 'Subtitle explaining connecting rooms availability disclaimer',
        })}
        icon={<LineDoorIcon />}
        checked={roomsConnected}
        inputPlacement="end"
        onChange={onConnectedRoomsChange}
      />,
    },
    {
      key: 'flights',
      visible: flightsEnabled && !charterBundleRoute,
      element: <SwitchListItem
        title={luxPlusFlightDiscountAvailable
          ? <Group direction="vertical" gap={4}>
              <LuxPlusLabel type="member-price-offer-flight" />
              {flightTitle}
          </Group>
          : flightTitle}
        subtitle={flightSubtitle}
        icon={<LinePlaneDepartureIcon />}
        checked={bundledWithFlightsOnly || flights}
        inputPlacement="end"
        onChange={bundledWithFlightsOnly ? undefined : onFlightsChange}
      />,
    },
  ].filter(toggle => toggle.visible)

  if (toggles.length === 0) {
    return null
  }

  return <Sheet background="normal-background">
    {toggles.map((toggle, i) => <Fragment key={toggle.key}>
      {i > 0 && <DividerContainer><Divider kind="primary" /></DividerContainer>}
      {toggle.element}
    </Fragment>)}
  </Sheet>
}

function OfferPageTogglesSectionWithOfferPrice(props: Omit<Props, 'flightPricePerPerson'>) {
  const canRenderFlightPrice = !!props.flightsEnabled &&
    !props.charterBundleRoute &&
    !props.bundledWithFlightsOnly
  const displayFlightPrice = useOfferPageFlightToggleDisplayPrice({
    airportCode: props.airport?.code,
    disabled: !canRenderFlightPrice,
  })

  return <OfferPageTogglesSectionContainer
    airport={props.airport}
    bundledWithFlightsOnly={props.bundledWithFlightsOnly}
    charterBundleRoute={props.charterBundleRoute}
    displayFlightPrice={displayFlightPrice}
    flights={props.flights}
    flightsEnabled={props.flightsEnabled}
    roomsConnected={props.roomsConnected}
  />
}

function OfferPageTogglesSectionContainer(props: ContentProps) {
  const hasMultipleRooms = useContext(OfferPageStateContext).rooms.length > 1
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onConnectedRoomsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [dispatch])

  const onFlightsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.setFlights, flights: !props.flights })
  }, [dispatch, props.flights])

  const onChangeAirport = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
    trackEvent('AIRPORT_FILTER_PRESSED')
  }, [dispatch, trackEvent])

  return <OfferPageTogglesSectionView
    airport={props.airport}
    bundledWithFlightsOnly={props.bundledWithFlightsOnly}
    charterBundleRoute={props.charterBundleRoute}
    displayFlightPrice={props.displayFlightPrice}
    flights={props.flights}
    flightsEnabled={props.flightsEnabled}
    hasMultipleRooms={hasMultipleRooms}
    onChangeAirport={onChangeAirport}
    onConnectedRoomsChange={onConnectedRoomsChange}
    onFlightsChange={onFlightsChange}
    roomsConnected={props.roomsConnected}
  />
}

function OfferPageTogglesSection(props: Props) {
  const rooms = useContext(OfferPageStateContext).rooms

  if (props.flightPricePerPerson !== undefined) {
    return <OfferPageTogglesSectionContainer
      airport={props.airport}
      bundledWithFlightsOnly={props.bundledWithFlightsOnly}
      charterBundleRoute={props.charterBundleRoute}
      displayFlightPrice={{ amount: props.flightPricePerPerson, unit: 'person', source: 'calculated' }}
      flights={props.flights}
      flightsEnabled={props.flightsEnabled}
      roomsConnected={props.roomsConnected}
    />
  }

  if (props.totalFlightPrice !== undefined) {
    const flightPricePerPerson = getFlightPricePerPerson(props.totalFlightPrice, rooms)

    return <OfferPageTogglesSectionContainer
      airport={props.airport}
      bundledWithFlightsOnly={props.bundledWithFlightsOnly}
      charterBundleRoute={props.charterBundleRoute}
      displayFlightPrice={flightPricePerPerson ? { amount: flightPricePerPerson, unit: 'person', source: 'legacyTotal' } : undefined}
      flights={props.flights}
      flightsEnabled={props.flightsEnabled}
      roomsConnected={props.roomsConnected}
    />
  }

  return <OfferPageTogglesSectionWithOfferPrice
    airport={props.airport}
    bundledWithFlightsOnly={props.bundledWithFlightsOnly}
    charterBundleRoute={props.charterBundleRoute}
    flights={props.flights}
    flightsEnabled={props.flightsEnabled}
    roomsConnected={props.roomsConnected}
  />
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'roomsConnected'
  | 'flights'
  | 'flightsEnabled'
  | 'charterBundleRoute'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  roomsConnected: state.isMultiRoomConnected,
  flights: state.flights,
  flightsEnabled: offerFlightsEnabled(state, appState),
  charterBundleRoute: state.charterBundleRoute,
}), OfferPageTogglesSection)
